import React from "react";
import { Link, LinkProps, useLocation } from "react-router-dom";
import { parsePath } from "history";
import { RETURN_MARKER, setReturnTrail } from "../../../helpers/returnTrail";
import { karkhanaPageFor } from "../karkhanaPaths";

type Here = { pathname: string; search?: string; hash?: string };

const PRODUCT_PAGES = ["product", "edit-product", "view-product"];

/** What a link out of Karkhana points at: a product (by id) or a family of pages (/looms…). */
export const trailTargetFor = (to: string): { productIds: string[]; paths: string[] } => {
  const pathname = to.split(/[?#]/)[0];
  const [, first, second] = pathname.split("/");
  if (PRODUCT_PAGES.includes(first) && second) return { productIds: [decodeURIComponent(second)], paths: [] };
  // Only pages outside Karkhana take a trail (a Karkhana page has its own Back).
  if (!first || first === "karkhana") return { productIds: [], paths: [] };
  return { productIds: [], paths: [first === "studio" && second ? `/${first}/${second}` : `/${first}`] };
};

/**
 * Before leaving Karkhana for a product, edit-product, looms or studio-modules page: remember the
 * exact address (path, query and hash), so that page's Back comes back here, not to its own list.
 * `extraProductIds` lets one page cover a product family (a costing's variants).
 */
export const rememberKarkhanaHere = (here: Here, to: string | null, label?: string, extraProductIds: string[] = []) => {
  const target = to ? trailTargetFor(to) : { productIds: [], paths: [] };
  if (!target.productIds.length && !target.paths.length && !extraProductIds.length) return; // staying in Karkhana
  const address = `${here.pathname}${here.search || ""}${here.hash || ""}`;
  const page = karkhanaPageFor(here.pathname);
  const name = label || (page && page.key !== "home" ? `Karkhana · ${page.screen}` : "Karkhana");
  setReturnTrail(address, name, [...target.productIds, ...extraProductIds], target.paths);
};

/** Where a link out of Karkhana goes, marked so that page's Back is the browser's Back to here. */
export const withReturnMarker = (to: string) => ({ ...parsePath(to), state: { [RETURN_MARKER]: true } });

/** A link out of Karkhana that leaves a return trail. Same look as a plain Link. */
export const OutLink = ({
  to,
  trailLabel,
  productIds,
  onClick,
  ...rest
}: Omit<LinkProps, "to"> & { to: string; trailLabel?: string; productIds?: string[] }) => {
  const location = useLocation();
  const leaving = trailTargetFor(to).paths.length > 0 || trailTargetFor(to).productIds.length > 0;
  return (
    <Link
      to={leaving ? withReturnMarker(to) : to}
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        // A new tab or window keeps this page open: no trail to follow.
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        rememberKarkhanaHere(location, to, trailLabel, productIds);
      }}
    />
  );
};
